import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import ShowGamesManager from "./ShowGamesManager"

export default function ShowsManager() {
  const navigate = useNavigate()
  const [shows, setShows] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedShowId, setExpandedShowId] = useState(null)

  const STATUSES = ["Waiting to begin", "First half", "Intermission", "Second half", "Complete"]

  useEffect(() => {
    fetchShows()
  }, [])

  const fetchShows = async () => {
    try {
      const response = await fetch("/api/admin/shows")
      const data = await response.json()
      setShows(data)
      setLoading(false)
    } catch (error) {
      console.error("Error fetching shows:", error)
      setLoading(false)
    }
  }

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await fetch(`/api/admin/shows/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify({ show: { status: newStatus } })
      })

      if (response.ok) {
        fetchShows()
      } else {
        const data = await response.json()
        alert(`Error: ${data.errors?.join(", ")}`)
      }
    } catch (error) {
      console.error("Error updating status:", error)
      alert("Error updating status")
    }
  }

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this show?")) return

    try {
      await fetch(`/api/admin/shows/${id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        }
      })
      fetchShows()
    } catch (error) {
      console.error("Error deleting show:", error)
      alert("Error deleting show")
    }
  }

  if (loading) {
    return <div className="text-center py-8">Loading...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Shows Management</h2>
        <button
          onClick={() => navigate("/admin/super_secret/shows/new")}
          className="bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg font-semibold"
        >
          + New Show
        </button>
      </div>

      <div className="space-y-4">
        {shows.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No shows yet.</p>
        ) : (
          shows.map((show) => (
            <div
              key={show.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-gray-800">
                      {show.title}
                    </h3>
                    {show.active && (
                      <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                        ACTIVE
                      </span>
                    )}
                    {show.status && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                        {show.status}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                    {show.theme && (
                      <div>
                        <span className="font-medium">Theme:</span> {show.theme}
                      </div>
                    )}
                    {show.date && (
                      <div>
                        <span className="font-medium">Date:</span>{" "}
                        {new Date(show.date).toLocaleDateString()}
                      </div>
                    )}
                    {show.ticket_url && (
                      <div className="col-span-2">
                        <span className="font-medium">Tickets:</span>{" "}
                        <a
                          href={show.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-600 hover:underline"
                        >
                          {show.ticket_url}
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={show.status || "Waiting to begin"}
                      onChange={(e) => handleStatusChange(show.id, e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                    >
                      {STATUSES.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-3 flex items-center gap-4">
                    <span className="text-sm text-gray-500">
                      {show.games?.length || 0} games | {show.ask_fors?.length || 0}{" "}
                      ask fors | {show.suggestions?.length || 0} suggestions | {" "}
                      {show.games?.reduce((sum, game) => sum + (game.votes_count || 0), 0) || 0} votes
                    </span>
                    <button
                      onClick={() => setExpandedShowId(expandedShowId === show.id ? null : show.id)}
                      className="text-purple-600 hover:text-purple-700 font-semibold text-sm flex items-center gap-1"
                    >
                      {expandedShowId === show.id ? (
                        <>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                          Hide Games
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                          Manage Games
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => navigate(`/admin/super_secret/shows/${show.id}/edit`)}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(show.id)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {expandedShowId === show.id && (
                <ShowGamesManager show={show} onUpdate={fetchShows} />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
